/**
 * What a plan found that its ticket did not ask for (ENG-2966): a nearby bug,
 * a security leak, a refactor. Never a question, a decision or a step of the
 * plan; the plan page lists them under "Found along the way", and a person
 * makes a ticket of any they want.
 *
 * The page is the one record: the backend writes the section from the plan's
 * `followUps` (`renderPlanFollowUpsSection`), the tracker's plan comment and
 * the ticket sheet read it back (`readPlanFollowUpsSection`), and the route
 * that makes a ticket of one finds it there by title, so a ticket is only
 * ever made of a follow-up the plan really named.
 */
export const PLAN_FOLLOW_UPS_SECTION = "Found along the way";
export const MAX_PLAN_FOLLOW_UPS = 10;
const MAX_TITLE = 200;
const MAX_WHY = 500;
const REPOSITORY = /^[\w.-]+\/[\w.-]+$/;
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** One line, no markdown emphasis that would break the list line it is written into. */
function line(value, max) {
    if (typeof value !== "string")
        return "";
    return value.replace(/\s+/g, " ").replace(/\*\*/g, "").trim().slice(0, max).trim();
}
function repositoryOf(value) {
    const name = line(value, 200)
        .replace(/^https?:\/\/github\.com\//i, "")
        .replace(/\.git$/i, "")
        .replace(/^\/+|\/+$/g, "");
    return REPOSITORY.test(name) ? name : null;
}
/**
 * The plan's `followUps` as written: one per distinct title (case aside), in
 * order, at most {@link MAX_PLAN_FOLLOW_UPS}. Total: anything else is none.
 */
export function readPlanFollowUps(value) {
    const out = [];
    const seen = new Set();
    for (const entry of Array.isArray(value) ? value : []) {
        if (!isRecord(entry))
            continue;
        const title = line(entry.title, MAX_TITLE);
        if (!title || seen.has(title.toLowerCase()))
            continue;
        seen.add(title.toLowerCase());
        const repository = repositoryOf(entry.repository);
        out.push({ title, why: line(entry.why, MAX_WHY), ...(repository ? { repository } : {}) });
        if (out.length === MAX_PLAN_FOLLOW_UPS)
            break;
    }
    return out;
}
function followUpLine(followUp) {
    const where = followUp.repository ? ` in \`${followUp.repository}\`` : "";
    return `- **${followUp.title}**${where}${followUp.why ? `: ${followUp.why}` : ""}`;
}
/** The plan page's "Found along the way" section, or null when there is nothing in it. */
export function renderPlanFollowUpsSection(followUps) {
    const list = readPlanFollowUps(followUps);
    if (list.length === 0)
        return null;
    return [
        `## ${PLAN_FOLLOW_UPS_SECTION}`,
        "",
        "Not part of this plan. Make a ticket of any you want.",
        "",
        ...list.map(followUpLine),
    ].join("\n");
}
const LINE = /^- \*\*(.+?)\*\*(?: in `([\w.-]+\/[\w.-]+)`)?(?:: (.*))?$/;
/** The follow-ups a plan page lists, read back off its "Found along the way" section. */
export function readPlanFollowUpsSection(markdown) {
    if (!markdown)
        return [];
    const out = [];
    let inside = false;
    for (const raw of markdown.replace(/\r\n/g, "\n").split("\n")) {
        const heading = /^##\s+(.+)$/.exec(raw);
        if (heading) {
            inside = heading[1].trim().toLowerCase() === PLAN_FOLLOW_UPS_SECTION.toLowerCase();
            continue;
        }
        if (!inside)
            continue;
        const match = LINE.exec(raw.trim());
        if (!match)
            continue;
        out.push({
            title: match[1].trim(),
            why: (match[3] ?? "").trim(),
            ...(match[2] ? { repository: match[2] } : {}),
        });
    }
    return readPlanFollowUps(out);
}
//# sourceMappingURL=planFollowUps.js.map